/** @jsxImportSource @emotion/react */
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { useParams } from "react-router-dom";
import { UNIT_OF_ACCOUNT } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import MetaForm from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";
import CategoryConfigForm from "./CategoryConfigForm";
import FieldConfigForm from "./FieldConfigForm";
import SanitizeUnexpectedFields from "./SanitizeUnexpectedFields";


export default function FruitTypeConfigForm({ config_data, setConfigData }) {
    const config = useConfig();
    const params = useParams();
    const { tenant_id } = params;

    const buttons = !config_data.value ? [] : [
        {
            label: `Selectable Fruit types`,
            name: `fruit_type`,
        },
        {
            label: `Defects internal (Deprecated)`,
            name: `defects_${config_data.value}_internal`,
        },
        {
            label: `Defects external (Deprecated)`,
            name: `defects_${config_data.value}_external`
        },
        {
            label: `Taste`,
            name: `manual_taste_${config_data.value}`
        },
        {
            label: `Maturity`,
            name: `manual_maturity_${config_data.value}`
        },
        {
            label: `PLU`,
            name: `plu_${config_data.value}`
        },
        {
            label: `Flavour nuttiness`,
            name: `flavour_nuttiness_${config_data.value}`
        },
        {
            label: `Flavour creamy`,
            name: `flavour_creamy_${config_data.value}`
        },
        {
            label: "Mouth ripeness",
            name: `mouth_ripeness_${config_data.value}`
        }
    ];

    const form = [
        {
            type: "header",
            label: "General configuration",
        },
        {
            label: "Value",
            description: "What is the slug or identifier of this fruit type. Used in the database and in urls. Please use singular form with lower case letters (e.g. apple, avocado, cherry, plum).",
            name: "value",
            createable: true,
            type: "text",
            onchange_callback: ({ value }) => {
                setConfigData({ ...config_data,
                    value: value.toLowerCase().replace(/ /g, "_").replace(/[^a-zA-Z_]/g, ""),
                });
            }
        },
        {
            label: "Label",
            description: "What is the label of this fruit type. Used in the button.",
            name: "text",
            type: "text"
        },
        {
            label: "A fruit",
            name: "a_fruit",
            type: "text",
            description: "What is the singular form of this fruit type. (e.g. an avocado, a mango, a kiwi)",
        },
        {
            label: "N fruit",
            name: "n",
            type: "text",
            description: "What is the plural form of this fruit type. (e.g. avocados, mangos, cherries)",
        },
        {
            label: "N cap",
            type: "text",
            name: "n_cap",
            description: "What is the plural form of this fruit type with a capital. (Avocados, Mangos, Cherries)",
        },
        {
            label: "Unit of account",
            description: "Default unit of account for this fruit type (fruit or box). This will be used as a fallback if not specified in the check location config. For example, bananas are typically checked by box even during intake.",
            name: "unit_of_account",
            type: "single-select",
            options: Object.keys(UNIT_OF_ACCOUNT).map((i) => ({ value: UNIT_OF_ACCOUNT[i], label: i }))
        },
        {
            type: "element",
            name: "ripening_fields",
            label: "Ripening Fields",
            description: "Ripening fields used in the ripening graph & ripening room.",
            el: <FieldConfigForm
                title="Ripening Fields"
                useDisplayFilter={undefined}
                description="Ripening fields used in the ripening graph & ripening room."
                name="ripening_fields"
                setArray={(ripening_fields) => setConfigData({ ...config_data, ripening_fields })}
                fields={config_data.ripening_fields || []}
            />,
        },
        {
            type: "header",
            label: "Radio and Dropdown options"
        },
        {
            type: "element",
            label: "Radio and dropdown options",
            description: buttons?.map((item) => item.label.toLowerCase()).join(", ") ?? "",
            el: <div>
                {buttons.map((i, idx) => (
                    <Button
                        key={idx}
                        color="light"
                        className="me-2 mb-2"
                        size="sm"
                        onClick={() => window.open(`/tenants/${tenant_id}/form-field-options/${i.name}`)}
                        icon={faArrowUpRightFromSquare}
                    >
                        {i.label}
                    </Button>
                ))}

                <Button
                    color="light"
                    className="me-2 mb-2"
                    size="sm"
                    onClick={() => window.open(`/tenants/${tenant_id}/defects/${config_data.value}`)}
                    icon={faArrowUpRightFromSquare}
                >
                    Defects
                </Button>
            </div>
        },
        {
            type: "header",
            label: "Deprecated fields",
            name: "deprecated_fields"
        },
        {
            type: "element",
            name: "external_colors",
            label: "External colors",
            description: "Configure radio button for external colors.",
            el: <CategoryConfigForm
                fields={config_data.external_colors || []}
                description="DEPRECATED: configure categories on your (ripening) field instead. Configure radio button for external colors"
                useDisplayFilter={false}
                name="external_colors"
                title="External colors"
                setArray={(external_colors) => setConfigData({ ...config_data, external_colors })} />
        },
        {
            type: "element",
            name: "internal_colors",
            label: "Internal colors",
            description: "Configure radio button for internal colors.",
            el: <CategoryConfigForm
                fields={config_data.internal_colors || []}
                description="DEPRECATED: configure categories on your (ripening) field instead. Configure radio button for internal colors"
                name="internal_colors"
                useDisplayFilter={false}
                title="Internal colors"
                setArray={(internal_colors) => setConfigData({ ...config_data, internal_colors })} />
        },
    ];

    return (
        <div className="d-flex flex-column gap-5">
            <SanitizeUnexpectedFields form={form} object={config_data} setObject={setConfigData} />

            <MetaForm
                withSearch
                groupFields
                meta={form}
                setValue={(field, value) => setConfigData({ ...config_data, [field]: value })}
                object={config_data}
                config={config}
            />
        </div>
    );
}
